/* ==========================================================================
   PDP v2 — shared behaviour for the pdp-v2-* section family.

   Everything here is delegated from document, so a section re-rendered by the
   theme editor (or by product-rerender) keeps working without re-binding.
   That is the same trap that broke the sticky ATC's submit listener.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__pdpV2Init) return;
  window.__pdpV2Init = true;

  /* The mobile design (`Mag7 PDP Mobile final.html`) and the desktop one are
     different pages below and above 1150px, which is the breakpoint the whole
     stylesheet already turns on. Declared here rather than beside the mobile
     code because the flip handler below is the first thing that needs it. */
  var mobile = window.matchMedia('(max-width: 1149px)');

  /* ---- flip cards ------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var card = event.target.closest('[data-pdp-v2-flip]');
    if (!card) return;
    // Mobile turns the confidence grid into a coverflow, where a tap on a side
    // card brings it to the centre instead of flipping it in place. Only the
    // centre card flips; initConfidence() does the centring. The merchant's
    // "Grid" layout has no coverflow at all -- every card sits in place and
    // taps it directly -- so this only applies to the slider layout.
    if (
      mobile.matches &&
      card.classList.contains('pdp-v2-conf__card') &&
      !card.classList.contains('is-center') &&
      card.closest('[data-pdp-v2-conf-layout="slider"]')
    ) {
      return;
    }
    var opening = card.getAttribute('aria-expanded') !== 'true';
    card.setAttribute('aria-expanded', opening ? 'true' : 'false');

    // The formula ticker only ever shows one flipped-open card at a time:
    // opening one closes any other card already open in the same strip.
    // Scoped to [data-pdp-v2-ticker] so it never touches the confidence
    // cards, which share this same flip handler but flip independently.
    if (opening) {
      var ticker = card.closest('[data-pdp-v2-ticker]');
      if (ticker) {
        ticker.querySelectorAll('[data-pdp-v2-flip][aria-expanded="true"]').forEach(function (other) {
          if (other !== card) other.setAttribute('aria-expanded', 'false');
        });
      }
    }
  });

  /* ---- formula ticker: a native horizontal scroller, plus a dot nav
     standing in for its own (hidden) scrollbar -----------------------------
     The strip itself (`data-pdp-v2-ticker-wrap`) is a plain `overflow-x:
     auto` container -- mouse wheel, trackpad, touch drag and keyboard all
     move it for free. Every real card renders exactly once (see the .liquid
     comment) and is a real `<button>` at all times, so the flip handler
     above works on every one of them.

     Five things happen here, all scoped to one wrap element:
       1. A dot nav, capped at 4 stops regardless of how many cards there
          are (built here, since the count is merchant-editable) replaces
          the strip's own hidden scrollbar -- the same dot-nav pattern the
          gallery/facts/confidence carousels already use elsewhere on this
          page. The nearest card also gets marked `.is-on` for its own quiet
          mobile-only glow -- see the CSS.
       2. A pointer-drag handler lets a mouse user grab the strip and pull it
          left/right, since a plain mouse (unlike touch/trackpad) has no
          native way to drag-scroll a horizontal container. It deliberately
          does NOT call setPointerCapture: capturing the pointer here also
          retargets the browser's own `click` event to this wrap element
          instead of the card under the cursor, which is what silently broke
          every card's flip the moment that call was added.
       3. The strip loops infinitely WITHOUT copying any card. It used to add
          a cloned set of cards on each side of the real ones, and since
          search engines run the page's JavaScript, every ingredient was read
          three times. Now the strip only ever holds the real cards, each
          once: when a card scrolls out of view at one end it is MOVED (not
          copied) to the other end and the scroll position is corrected by
          that card's width -- nothing on screen visibly moves. Auto-scroll
          idles it to the right forever; wheel, drag and touch can go either
          way without ever hitting an end.
       4. Both the drag handler and the wheel handler clamp their target to
          `[0, the strip's own real max scroll]`, so neither can be pushed
          past either end.
       5. Dragging past a small threshold suppresses the click that would
          otherwise land on whatever card the drag happened to end over. */
  function initTickerAuto() {
    document.querySelectorAll('[data-pdp-v2-ticker-wrap]').forEach(function (wrapEl) {
      if (wrapEl._pdpV2TickerInit) return;
      wrapEl._pdpV2TickerInit = true;

      var cards = Array.prototype.slice.call(
        wrapEl.querySelectorAll('.pdp-v2-formula-collagen__ticker-card')
      );
      var progressEl = wrapEl.parentElement
        ? wrapEl.parentElement.querySelector('[data-pdp-v2-ticker-progress]')
        : null;

      function maxScroll() {
        return wrapEl.scrollWidth - wrapEl.clientWidth;
      }

      /* ---- infinite loop, no duplicate cards (see item 3 above) -------- */
      var strip = wrapEl.querySelector('[data-pdp-v2-ticker]');
      var allCards = cards;

      // One card's share of the strip: its width plus the flex gap. Measured
      // live, so a resize (card widths change at the breakpoint) never needs
      // re-initialising.
      function gapPx() {
        var cs = window.getComputedStyle(strip);
        return parseFloat(cs.columnGap || cs.gap) || 0;
      }
      function stepOf(card, gap) {
        return card.offsetWidth + gap;
      }

      // Hiding a card's move needs at least one card's width of spare scroll
      // room, so the strip has to overflow by more than its widest card.
      // When it does not, it scrolls once to the end and stops, as a strip
      // that does not overflow at all always did.
      function canLoop() {
        if (!strip || cards.length < 2) return false;
        var gap = gapPx();
        var widest = 0;
        for (var i = 0; i < cards.length; i++) widest = Math.max(widest, stepOf(cards[i], gap));
        return maxScroll() >= widest;
      }
      var looping = canLoop();

      // One full set's width, gap included.
      function setWidth() {
        if (!looping) return 0;
        var gap = gapPx();
        var w = 0;
        for (var i = 0; i < cards.length; i++) w += stepOf(cards[i], gap);
        return w;
      }

      // Where the strip's left edge would be if no card had been moved: the
      // original position of whichever card is first in the strip right now,
      // plus how far the strip is scrolled. Drives the dots.
      function logicalLeft() {
        var gap = gapPx();
        var first = strip.firstElementChild;
        var x = 0;
        for (var i = 0; i < cards.length && cards[i] !== first; i++) x += stepOf(cards[i], gap);
        return x + wrapEl.scrollLeft;
      }

      var dragStartScroll = 0;
      var pos = 0;
      // A dot click's smooth scroll would be cut short by a jump mid-glide,
      // so wrapping waits until it lands. Its target is at most half a set
      // away, which always fits inside the three-set strip.
      var gliding = false;
      var glide = null;

      // Keeps the strip's position within about a card of the middle of its
      // own scroll range: drifted past it to the right, the first card moves
      // to the end; to the left, the last card moves to the front. Each move
      // is paired with a scroll correction of exactly that card's width, so
      // what is on screen stays put. Shifts the drag origin, the auto-scroll
      // accumulator and any dot glide by the same amount, so none of them
      // notice. A card holding keyboard focus is never moved (moving it
      // would drop the focus).
      function wrapLoop() {
        if (!looping) return;
        var gap = gapPx();
        var mid = maxScroll() / 2;
        for (var guard = 0; guard < cards.length; guard++) {
          var x = wrapEl.scrollLeft;
          var first = strip.firstElementChild;
          var last = strip.lastElementChild;
          var shift = 0;
          if (x >= mid + stepOf(first, gap) / 2) {
            if (first.contains(document.activeElement)) return;
            shift = -stepOf(first, gap);
            strip.appendChild(first);
          } else if (x < mid - stepOf(last, gap) / 2) {
            if (last.contains(document.activeElement)) return;
            shift = stepOf(last, gap);
            strip.insertBefore(last, first);
          } else {
            return;
          }
          wrapEl.scrollTo({ left: x + shift, behavior: 'instant' });
          pos += shift;
          dragStartScroll += shift;
          if (glide) {
            glide.from += shift;
            glide.to += shift;
          }
        }
      }

      if (looping) {
        // The browser's own scroll anchoring must not "help" by correcting
        // the position a second time when a card moves.
        wrapEl.style.overflowAnchor = 'none';
        // Opens with the first card at the left edge, as before -- the cards
        // brought round in front of it sit off-screen to the left.
        wrapLoop();
      }

      // A dot click's glide, driven frame by frame (like auto-scroll) rather
      // than by the browser's smooth scroll, which a card move mid-glide
      // would cut short. Any drag, wheel or touch takes over from it.
      function glideBy(delta) {
        var g = { from: wrapEl.scrollLeft, to: wrapEl.scrollLeft + delta, t0: null };
        glide = g;
        gliding = true;
        function frame(now) {
          if (glide !== g) return;
          if (g.t0 === null) g.t0 = now;
          var t = Math.min(1, (now - g.t0) / 500);
          var e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
          wrapEl.scrollTo({ left: clampX(g.from + (g.to - g.from) * e), behavior: 'instant' });
          wrapLoop();
          if (t < 1) {
            requestAnimationFrame(frame);
          } else {
            glide = null;
            gliding = false;
            sync();
          }
        }
        requestAnimationFrame(frame);
      }
      function stopGlide() {
        glide = null;
        gliding = false;
      }
      ['pointerdown', 'wheel', 'touchstart'].forEach(function (type) {
        wrapEl.addEventListener(type, stopGlide, { passive: true });
      });

      function clampX(x) {
        return Math.max(0, Math.min(maxScroll(), x));
      }

      // How far along the strip is, as a plain 0-1 fraction -- both index
      // functions below just multiply this by however many stops THEY have,
      // so the first stop is 0 and the last is 1 for card and dot alike, no
      // matter how those two counts differ.
      // Looping, it is the position within ONE set instead, wrapping 0-1.
      function scrollFraction() {
        if (looping) {
          var w = setWidth();
          if (w <= 0) return 0;
          return (((logicalLeft() % w) + w) % w) / w;
        }
        var denom = maxScroll();
        if (denom <= 0) return 0;
        return Math.max(0, Math.min(1, wrapEl.scrollLeft / denom));
      }

      // A dot per real card looked cluttered with more than a handful of
      // ingredients, and on mobile the whole row could run wider than the
      // screen -- the last dot (and the "last card reached" feedback it
      // gave) was simply off past the edge. Capped at 4 regardless of card
      // count: one moving "bar" (the CSS already elongates `.is-on`) that
      // sweeps across a fixed, always-visible set of stops instead.
      var DOT_COUNT = Math.min(4, cards.length);

      var dots = [];
      if (progressEl && cards.length > 1) {
        // Was `aria-hidden="true"` on a plain progress bar; now a real
        // (small) nav, so it needs to be reachable rather than hidden.
        progressEl.removeAttribute('aria-hidden');
        progressEl.setAttribute('role', 'group');
        progressEl.setAttribute('aria-label', 'Scroll through ingredients');
        progressEl.innerHTML = '';
        var _loop = function (index) {
          var dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'pdp-v2-formula-collagen__ticker-dot';
          dot.setAttribute('aria-label', 'Scroll to ' + Math.round((index / (DOT_COUNT - 1 || 1)) * 100) + '%');
          dot.addEventListener('click', function () {
            var target;
            if (looping) {
              // Evenly round the loop, taking the short way from where the
              // strip is now.
              var w = setWidth();
              if (w <= 0) return;
              var delta = (index / DOT_COUNT) * w - logicalLeft();
              delta = ((delta % w) + w) % w;
              if (delta > w / 2) delta -= w;
              glideBy(delta);
              return;
            } else {
              target = DOT_COUNT > 1 ? (index / (DOT_COUNT - 1)) * maxScroll() : 0;
            }
            wrapEl.scrollTo({ left: clampX(target), behavior: 'smooth' });
          });
          progressEl.appendChild(dot);
          dots.push(dot);
        };
        for (var d = 0; d < DOT_COUNT; d++) _loop(d);
      }

      // Proportional to how far scrolled, not "whichever card is nearest
      // the middle of the wrap" (what this used to do) -- with more cards
      // than fit in one screen, the first card's own centre never actually
      // reaches the wrap's centre (it is still pinned to the left edge when
      // scrollLeft is 0), and the same is true in reverse for the last
      // card. That left the first and last position permanently
      // unreachable. A straight scroll-fraction maps 0 to the very start and
      // 1 to the very end exactly, so every stop (card OR dot, whichever
      // count is asked for) gets its turn, including the last one.
      function fractionIndex(count) {
        if (count < 2) return 0;
        return Math.max(0, Math.min(count - 1, Math.round(scrollFraction() * (count - 1))));
      }

      // Looping has no first/last edge to worry about, so the glow goes to
      // whichever card sits nearest the strip's centre.
      function centreIndex() {
        var mid = wrapEl.scrollLeft + wrapEl.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        for (var i = 0; i < allCards.length; i++) {
          var c = allCards[i];
          var dist = Math.abs(c.offsetLeft + c.offsetWidth / 2 - mid);
          if (dist < bestDist) {
            bestDist = dist;
            best = i;
          }
        }
        return best;
      }

      function sync() {
        if (!cards.length) return;
        if (looping) {
          mark(allCards, centreIndex());
          if (dots.length) mark(dots, Math.round(scrollFraction() * dots.length) % dots.length);
          return;
        }
        // Two different counts, the same fraction -- the card glow (mobile
        // only, see the CSS) still tracks the actual nearest card, while the
        // dot nav below it only ever has to land on one of its 4 stops.
        mark(cards, fractionIndex(cards.length));
        if (dots.length) mark(dots, fractionIndex(dots.length));
      }

      sync();
      window.addEventListener('resize', function () {
        wrapLoop();
        sync();
      });

      var ticking = false;
      wrapEl.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(function () {
            wrapLoop();
            sync();
            ticking = false;
          });
        },
        { passive: true }
      );

      /* ---- mouse drag-to-scroll -------------------------------------- */
      var dragging = false;
      var dragMoved = false;
      var dragStartX = 0;

      wrapEl.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'touch') return; // native touch scroll already handles this
        dragging = true;
        dragMoved = false;
        dragStartX = event.clientX;
        dragStartScroll = wrapEl.scrollLeft;
        wrapEl.classList.add('is-dragging');
      });

      // On `window`, not `wrapEl` -- the drag keeps tracking even if the
      // pointer strays outside the strip mid-gesture, without needing
      // setPointerCapture (see the header comment for why that is out).
      window.addEventListener('pointermove', function (event) {
        if (!dragging) return;
        var dx = event.clientX - dragStartX;
        if (Math.abs(dx) > 3) dragMoved = true;
        // 'instant', not a plain `scrollLeft =` assignment: the wrap now
        // carries CSS `scroll-behavior: smooth` (for keyboard/native
        // scrolling), and that setter obeys it too -- every drag frame
        // would kick off its own smooth-animated scroll and stack up into a
        // laggy trail behind the cursor instead of tracking it 1:1.
        // clampX() keeps the drag within the strip's own real scroll range.
        wrapEl.scrollTo({ left: clampX(dragStartScroll - dx), behavior: 'instant' });
      });

      function endDrag() {
        if (!dragging) return;
        dragging = false;
        wrapEl.classList.remove('is-dragging');
        // Keeps `.is-auto` in step with the combined hovering/dragging state
        // even though `hovering` itself did not change here.
        wrapEl.classList.toggle('is-auto', !hovering);
      }
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);

      // A card's own click (which flips it -- see the delegated handler
      // above) must not fire at the end of a drag gesture that happened to
      // release over that card.
      wrapEl.addEventListener(
        'click',
        function (event) {
          if (dragMoved) {
            event.stopPropagation();
            event.preventDefault();
            dragMoved = false;
          }
        },
        true
      );

      /* ---- mouse wheel: vertical input scrolls the strip horizontally --
         A plain mouse only turns a VERTICAL wheel. Without this, hovering the
         strip and scrolling just scrolls the page -- the wrap has no
         vertical overflow of its own to catch that input -- which is what
         read as the strip "not scrolling smoothly" with the cursor. A
         trackpad's own horizontal swipe (deltaX) is left to the browser's
         native (already smooth) handling; only a vertical-dominant wheel
         tick gets redirected here, animated with the browser's own smooth
         scroll rather than jumping straight to the new position. */
      wrapEl.addEventListener(
        'wheel',
        function (event) {
          if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
          event.preventDefault();
          // Computed and set explicitly, not scrollBy(), so the clamp
          // (keeping the wheel within the strip's own real scroll range)
          // applies to where the scroll is actually headed.
          wrapEl.scrollTo({ left: clampX(wrapEl.scrollLeft + event.deltaY), behavior: 'smooth' });
        },
        { passive: false }
      );

      /* ---- gentle auto-scroll: forever when looping, otherwise first
         card to last, then stops ------------------------------------- */
      // CSS scroll-snap treats a programmatic `scrollLeft` write as a
      // completed scroll and snaps it straight back to the nearest card on
      // every single frame -- which is what made the auto-scroll never
      // actually move at all rather than just move slowly. `.is-auto`
      // (added below) turns scroll-snap off for the span of time the strip
      // is driving itself, and back on the moment a visitor takes over.
      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      var hovering = false;
      var resumeTimer = null;
      var SPEED = 40; // px per SECOND, not per frame -- see step() below
      // A float accumulator, not `wrapEl.scrollLeft` read back each frame --
      // the browser can round or (while snap was fighting it) silently
      // reject that read, which made a sub-pixel-per-frame speed compound
      // into no movement at all.
      pos = wrapEl.scrollLeft;
      var lastTime = null;
      // Never set while looping. Otherwise: set for good the moment
      // auto-scroll's own motion first reaches the last card -- it never resets, so auto-scroll never restarts on its
      // own after that, even if a shopper later scrolls back toward the
      // start by hand. That hand scroll still works at any time; it just
      // never wakes auto-scroll back up.
      var reachedEnd = false;

      function setHovering(next) {
        hovering = next;
        wrapEl.classList.toggle('is-auto', !hovering && !dragging);
      }

      // `now` (a rAF timestamp, in ms) drives movement by elapsed time
      // rather than by a fixed amount per call -- a per-frame constant looks
      // fine at a steady 60fps but visibly stutters on any display or tab
      // that skips frames (a 120Hz screen, a busy main thread), since the
      // strip would still only move that one fixed amount on whichever
      // frames do land, in bigger, less frequent steps.
      // Cheap: this only ever has to look inside one ticker's own small
      // strip, and only on the frames auto-scroll would otherwise actually
      // move.
      function anyCardOpen() {
        return !!wrapEl.querySelector('[data-pdp-v2-flip][aria-expanded="true"]');
      }

      function step(now) {
        if (!hovering && !dragging && !gliding && !anyCardOpen() && !reachedEnd) {
          if (lastTime !== null) {
            pos += (SPEED * (now - lastTime)) / 1000;
            var limit = maxScroll();
            if (!looping && pos >= limit) {
              pos = limit;
              reachedEnd = true;
            }
            // 'instant', same reason as the drag handler above -- the CSS
            // smooth scroll-behavior would otherwise animate every single
            // one of these small per-frame nudges and the strip would
            // never actually keep up with its own intended position.
            wrapEl.scrollTo({ left: pos, behavior: 'instant' });
            // Jumps back a set (shifting `pos` with it) once it is due.
            wrapLoop();
          }
        } else {
          // A visitor is driving the strip by hand (hover-wheel, drag or
          // touch), or auto-scroll has already reached the end -- keep the
          // accumulator caught up either way, so a resumed auto-scroll (the
          // hover/drag case) picks back up from wherever they left it
          // instead of jumping, and a manual scroll after reachedEnd is
          // never fought or overridden.
          pos = wrapEl.scrollLeft;
        }
        lastTime = now;
        requestAnimationFrame(step);
      }

      if (!reduceMotion) {
        wrapEl.classList.add('is-auto');
        wrapEl.addEventListener('pointerenter', function (event) {
          if (event.pointerType === 'touch') return;
          clearTimeout(resumeTimer);
          setHovering(true);
        });
        wrapEl.addEventListener('pointerleave', function (event) {
          if (event.pointerType === 'touch') return;
          setHovering(false);
        });
        // Touch has no hover -- pause while a finger is down, and give the
        // momentum scroll a beat to settle before auto-scroll picks back up,
        // rather than fighting it the instant the finger lifts.
        wrapEl.addEventListener(
          'touchstart',
          function () {
            clearTimeout(resumeTimer);
            setHovering(true);
          },
          { passive: true }
        );
        wrapEl.addEventListener(
          'touchend',
          function () {
            clearTimeout(resumeTimer);
            resumeTimer = setTimeout(function () {
              setHovering(false);
            }, 400);
          },
          { passive: true }
        );
        wrapEl.addEventListener('focusin', function () {
          clearTimeout(resumeTimer);
          setHovering(true);
        });
        wrapEl.addEventListener('focusout', function () {
          setHovering(false);
        });
        requestAnimationFrame(step);
      }
    });
  }

  initTickerAuto();
  document.addEventListener('shopify:section:load', initTickerAuto);

  /* ---- gallery thumb rail: mouse drag-to-scroll -------------------------
     Desktop's thumb row hides its scrollbar (matches the ticker's own look),
     and with more thumbnails than fit in the row (8 images, ~7 visible) a
     mouse had no way to reach the rest -- no scrollbar to grab, no drag
     handler. This is the same click-and-drag pattern initTickerAuto() above
     uses, without the auto-scroll or dot nav this row doesn't need: touch and
     trackpad already scroll it natively, a mouse now can too. */
  function initGalleryThumbScroll() {
    document.querySelectorAll('.pdp-v2-gallery__thumbs').forEach(function (el) {
      if (el._pdpV2ThumbScrollInit) return;
      el._pdpV2ThumbScrollInit = true;

      var dragging = false;
      var dragMoved = false;
      var dragStartX = 0;
      var dragStartScroll = 0;

      el.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'touch') return; // native touch scroll already handles this
        dragging = true;
        dragMoved = false;
        dragStartX = event.clientX;
        dragStartScroll = el.scrollLeft;
        el.classList.add('is-dragging');
      });

      window.addEventListener('pointermove', function (event) {
        if (!dragging) return;
        var dx = event.clientX - dragStartX;
        if (Math.abs(dx) > 3) dragMoved = true;
        el.scrollLeft = dragStartScroll - dx;
      });

      function endDrag() {
        if (!dragging) return;
        dragging = false;
        el.classList.remove('is-dragging');
      }
      window.addEventListener('pointerup', endDrag);
      window.addEventListener('pointercancel', endDrag);

      // A dragged-past-threshold gesture must not also fire the thumb's own
      // click (which switches the gallery to that picture).
      el.addEventListener(
        'click',
        function (event) {
          if (dragMoved) {
            event.stopPropagation();
            event.preventDefault();
            dragMoved = false;
          }
        },
        true
      );
    });
  }

  initGalleryThumbScroll();
  document.addEventListener('shopify:section:load', initGalleryThumbScroll);

  /* ---- icon highlights: custom scroll indicator -------------------------
     Stands in for the row's own native scrollbar, which pdp-v2.css hides
     outright: a native one is not a reliable "there's more" cue on mobile --
     iOS Safari never exposes ::-webkit-scrollbar at all, and its overlay
     scrollbar only flashes in during an actual scroll gesture, which cannot
     be forced to stay on screen from CSS alone. This draws its own track +
     thumb instead (data-pdp-v2-icons-track / -thumb, right after the row in
     the .liquid file), sized and positioned from the row's own scroll state
     rather than the browser's, so it is visible immediately on load and
     stays visible through the whole gesture on every platform alike. */
  function initIconsScrollbar() {
    document.querySelectorAll('[data-pdp-v2-icons-track]').forEach(function (track) {
      if (!claim(track)) return;
      var list = track.previousElementSibling;
      var thumb = track.querySelector('[data-pdp-v2-icons-thumb]');
      if (!list || !thumb) return;

      function sync() {
        var max = list.scrollWidth - list.clientWidth;
        // Nothing to scroll to (every icon already fits) -- no cue needed.
        if (max <= 0) {
          track.hidden = true;
          return;
        }
        track.hidden = false;
        var thumbFraction = Math.min(1, list.clientWidth / list.scrollWidth);
        var travel = 100 - thumbFraction * 100;
        thumb.style.width = thumbFraction * 100 + '%';
        thumb.style.left = (list.scrollLeft / max) * travel + '%';
      }

      sync();
      list.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      // A late webfont can change how many icons fit, and so whether the row
      // scrolls at all.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);
    });
  }

  initIconsScrollbar();
  document.addEventListener('shopify:section:load', initIconsScrollbar);

  /* ---- accordions (usage, formula, faq) -------------------------------- */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-pdp-v2-accordion]');
    if (!trigger) return;

    var open = trigger.getAttribute('aria-expanded') === 'true';
    var group = trigger.getAttribute('data-pdp-v2-accordion-group');

    // Single-open groups close their siblings first.
    if (!open && group) {
      document
        .querySelectorAll('[data-pdp-v2-accordion-group="' + CSS.escape(group) + '"]')
        .forEach(function (other) {
          if (other !== trigger) setPanel(other, false);
        });
    }
    setPanel(trigger, !open);
  });

  function setPanel(trigger, open) {
    var id = trigger.getAttribute('aria-controls');
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    // A panel can have two triggers (the hero's "Read more" above it and
    // "Read less" at its end), so every one of them follows the state.
    if (id) {
      document.querySelectorAll('[data-pdp-v2-accordion][aria-controls="' + CSS.escape(id) + '"]').forEach(function (other) {
        other.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
    var panel = document.getElementById(id);
    if (!panel) return;
    // Animate to the measured height, then release to auto so the panel can
    // reflow if its contents change (an image loading, a font swapping in).
    if (open) {
      panel.hidden = false;
      panel.style.height = panel.scrollHeight + 'px';
      panel.addEventListener('transitionend', function done(event) {
        // transitionend bubbles: a nested accordion finishing first would
        // otherwise snap this panel to auto mid-open.
        if (event.target !== panel || event.propertyName !== 'height') return;
        panel.style.height = 'auto';
        panel.removeEventListener('transitionend', done);
      });
    } else {
      panel.style.height = panel.scrollHeight + 'px';
      requestAnimationFrame(function () {
        panel.style.height = '0px';
      });
    }
  }

  /* ---- peek / show-more panels ------------------------------------------ */
  // A shorter cousin of the accordion above: the panel is never fully
  // hidden, only clipped to a peek height with a fade-out overlay, so there
  // is nothing to measure and no transitionend juggling -- CSS does the
  // whole animation off one class.
  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-pdp-v2-peek-toggle]');
    if (!toggle) return;
    var panel = document.getElementById(toggle.getAttribute('aria-controls'));
    var open = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    if (panel) panel.classList.toggle('is-open', !open);
  });

  /* ---- hero gallery ---------------------------------------------------- */
  function galleryOf(el) {
    return el.closest('[data-pdp-v2-gallery]');
  }

  // Slides hidden by the flavour filter below are display:none, so they take
  // no space in the track -- every scroll-position calculation has to work
  // in terms of this list, not the raw (unfiltered) child list, or it lands
  // on the wrong picture as soon as a flavour is selected.
  function galleryVisibleSlides(track) {
    return Array.prototype.filter.call(track.children, function (el) {
      return el.style.display !== 'none';
    });
  }

  // The visible slides in on-screen order. To loop, galleryLoop moves one
  // slide across the seam with CSS `order`: 'tail' puts the first slide after
  // the last, 'head' the last before the first. track.children never changes,
  // so every absolute index elsewhere stays valid.
  function galleryOrder(track) {
    var v = galleryVisibleSlides(track);
    if (track.dataset.loop === 'tail') return v.slice(1).concat(v[0]);
    if (track.dataset.loop === 'head') return [v[v.length - 1]].concat(v.slice(0, -1));
    return v;
  }

  // Run whenever the track is at rest. On the last slide the first one waits
  // after it, on the first slide the last one waits before it, so the next
  // swipe or arrow slides straight on round the loop instead of rewinding
  // through every picture. Moving a slide shifts the strip, so the scroll
  // position is put back on the same picture in the same frame.
  // ponytail: needs 3+ slides; with 2 the arrows still wrap, by rewinding.
  function galleryLoop(track) {
    var w = track.clientWidth;
    var v = galleryVisibleSlides(track);
    if (!w || v.length < 3) return;
    var at = track.scrollLeft / w;
    if (Math.abs(at - Math.round(at)) > 0.02) return; // mid-swipe or mid-glide
    var slide = galleryOrder(track)[Math.round(at)];
    if (!slide) return;
    var i = v.indexOf(slide);
    var mode = i === v.length - 1 ? 'tail' : i === 0 ? 'head' : '';
    if (mode === (track.dataset.loop || '')) return;
    v.forEach(function (el) { el.style.order = ''; });
    if (mode === 'tail') v[0].style.order = '1';
    if (mode === 'head') v[v.length - 1].style.order = '-1';
    track.dataset.loop = mode;
    track.scrollTo({ left: galleryOrder(track).indexOf(slide) * w, behavior: 'instant' });
  }

  // data-pdp-v2-gallery-go, data-media (pack rows) and the combo map's own
  // "media" field are all baked in as positions in the FULL, unfiltered
  // product.media list -- that list never changes, only which slides of it
  // are on screen does. Every entry point below still takes that absolute
  // index; only the scroll math in between goes through the visible list.
  function galleryGo(gallery, absoluteIndex, behavior) {
    var track = gallery.querySelector('[data-pdp-v2-gallery-track]');
    if (!track) return;
    var slide = track.children[absoluteIndex];
    if (!slide) return;
    var visible = galleryOrder(track);
    var pos = visible.indexOf(slide);
    if (pos === -1) pos = 0; // filtered out already -- land at the start rather than not moving
    // 'auto' is for the first paint, where sliding in from image one would be
    // an animation nobody asked for.
    track.scrollTo({ left: pos * track.clientWidth, behavior: behavior || 'smooth' });
    galleryMark(gallery, absoluteIndex);
  }

  function galleryMark(gallery, absoluteIndex) {
    gallery.querySelectorAll('[data-pdp-v2-gallery-go]').forEach(function (control) {
      control.classList.toggle('is-active', Number(control.getAttribute('data-pdp-v2-gallery-go')) === absoluteIndex);
    });
  }

  // Reads the slide the track is actually scrolled to (a visible position)
  // and converts it back to its absolute media index, since that is what
  // galleryMark / galleryGo both key on.
  function galleryIndex(gallery) {
    var track = gallery.querySelector('[data-pdp-v2-gallery-track]');
    if (!track || !track.clientWidth) return 0;
    var visible = galleryOrder(track);
    var pos = Math.round(track.scrollLeft / track.clientWidth);
    var slide = visible[pos];
    return slide ? Array.prototype.indexOf.call(track.children, slide) : 0;
  }

  // Prev/next step by one VISIBLE slide, not by absolute index +-1 -- with a
  // flavour filter active the neighbouring absolute index may itself be
  // hidden (or may not be adjacent at all in the visible run). Loops: galleryLoop
  // has the neighbour across the seam in place, and the modulo only matters
  // when it couldn't (2 slides).
  function galleryStep(gallery, delta) {
    var track = gallery.querySelector('[data-pdp-v2-gallery-track]');
    if (!track || !track.clientWidth) return;
    galleryLoop(track);
    var order = galleryOrder(track);
    var n = order.length;
    if (!n) return;
    var pos = (((Math.round(track.scrollLeft / track.clientWidth) + delta) % n) + n) % n;
    var slide = order[pos];
    if (!slide) return;
    galleryGo(gallery, Array.prototype.indexOf.call(track.children, slide));
  }

  // Hides every slide/dot/thumb tagged for a different flavour than the one
  // just selected. data-pdp-v2-media-flavours is a comma list of flavour
  // values a picture is linked to in admin; empty means it is shared (a
  // lifestyle shot, say) and stays on screen no matter which flavour is
  // active. A product with no flavour axis at all never calls this, so
  // nothing here ever runs for it and every slide stays as it was.
  function applyFlavourFilter(root) {
    var gallery = root.querySelector('[data-pdp-v2-gallery]');
    if (!gallery) return;
    var value = root.getAttribute('data-pdp-v2-current-flavour');
    if (!value) return;

    function matches(el) {
      var list = el.getAttribute('data-pdp-v2-media-flavours');
      return !list || (',' + list + ',').indexOf(',' + value + ',') !== -1;
    }

    var track = gallery.querySelector('[data-pdp-v2-gallery-track]');
    if (!track) return;

    // Safety net: if every slide is tagged for some OTHER flavour (or, on a
    // product whose pictures were never Alt-tagged, none qualify as shared
    // at all), the filter below would hide the whole track and leave an
    // empty slider with nothing to show or click through. A misconfigured
    // product still needs a gallery, so this falls back to showing every
    // slide rather than none.
    var anyMatch = Array.prototype.some.call(track.children, matches);

    Array.prototype.forEach.call(track.children, function (slide) {
      slide.style.display = !anyMatch || matches(slide) ? '' : 'none';
      slide.style.order = '';
    });
    track.dataset.loop = '';

    gallery.querySelectorAll('[data-pdp-v2-gallery-go]').forEach(function (control) {
      control.hidden = anyMatch && !matches(control);
    });
  }

  document.addEventListener('click', function (event) {
    var go = event.target.closest('[data-pdp-v2-gallery-go]');
    if (go) {
      galleryGo(galleryOf(go), Number(go.getAttribute('data-pdp-v2-gallery-go')));
      return;
    }
    var prev = event.target.closest('[data-pdp-v2-gallery-prev]');
    if (prev) {
      galleryStep(galleryOf(prev), -1);
      return;
    }
    var next = event.target.closest('[data-pdp-v2-gallery-next]');
    if (next) {
      galleryStep(galleryOf(next), 1);
    }
  });

  // A swipe can start before the track has settled once (first paint on
  // slide one), so seat the loop as a finger lands too.
  document.addEventListener('touchstart', function (event) {
    var track = event.target.closest && event.target.closest('[data-pdp-v2-gallery-track]');
    if (track) galleryLoop(track);
  }, { passive: true });

  /* ---- gallery zoom ---------------------------------------------------- */
  /* The same PhotoSwipe lightbox and options as the theme's product-gallery
     (theme.js, ProductGallery.photoswipe), so Mag 7 and Collagen zoom exactly
     like every other product page. PhotoSwipe itself is handed over as
     window.pdpV2Pswp by a module script in main-product-pdp-v2.liquid.
     Works at every width: the button, or a
     tap/click on the image itself, opens it.

     Only the slides currently on screen go into the lightbox -- Collagen's
     flavour filter hides other flavours' pictures (display:none), and the
     lightbox should page through the same set the gallery does. On Mag7
     nothing is ever filtered, so this is every slide, as before. */
  var zoomLightbox = null;

  function zoomGet() {
    if (zoomLightbox) return zoomLightbox;
    var pswp = window.pdpV2Pswp;
    if (!pswp) return null;
    var strings = (window.themeVariables && window.themeVariables.strings) || {};
    var lightbox = new pswp.Lightbox({
      pswpModule: pswp.module,
      bgOpacity: 1,
      maxZoomLevel: 3,
      closeTitle: strings.closeGallery,
      zoomTitle: strings.zoomGallery,
      errorMsg: strings.errorGallery,
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: '<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">' +
        '<path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>' +
        '<path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>' +
        '<path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>' +
        '</svg>'
    });
    lightbox.addFilter('thumbEl', function (thumbEl, data) { return data.thumbnailElement; });
    lightbox.on('uiRegister', function () {
      lightbox.pswp.ui.registerElement({
        name: 'bottom-bar',
        order: 5,
        appendTo: 'wrapper',
        html:
          '<div class="pagination">' +
            '<button class="pagination__item group" rel="prev"><span class="animated-arrow animated-arrow--reverse"></span></button>' +
            '<span class="pagination__current text-sm"><span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span></span>' +
            '<button class="pagination__item group" rel="next"><span class="animated-arrow"></span></button>' +
          '</div>',
        onInit: function (el, pswp) {
          el.querySelector('.pagination__page-count').innerText = pswp.getNumItems();
          el.querySelector('[rel="prev"]').addEventListener('click', function () { pswp.prev(); });
          el.querySelector('[rel="next"]').addEventListener('click', function () { pswp.next(); });
          pswp.on('change', function () {
            el.querySelector('.pagination__current-page').innerText = pswp.currIndex + 1;
          });
        }
      });
    });
    lightbox.init();
    return (zoomLightbox = lightbox);
  }

  // The visible slides' own pictures, and which of them is on screen now.
  function zoomImages(gallery) {
    var track = gallery.querySelector('[data-pdp-v2-gallery-track]');
    if (!track) return { images: [], index: 0 };
    var slides = galleryVisibleSlides(track);
    var images = [];
    slides.forEach(function (slide) {
      var img = slide.querySelector(':scope > img');
      if (img) images.push(img);
    });
    var current = track.children[galleryIndex(gallery)];
    var at = images.indexOf(current ? current.querySelector(':scope > img') : null);
    return { images: images, index: at < 0 ? 0 : at };
  }

  function zoomOpen(gallery) {
    var picked = zoomImages(gallery);
    var images = picked.images;
    if (!images.length) return;
    var lightbox = zoomGet();
    if (!lightbox) {
      // PhotoSwipe's module never arrived (blocked or failed to load). Better
      // than a dead button: the full-size picture in a new tab.
      var shown = images[picked.index];
      if (shown) window.open(shown.currentSrc || shown.src, '_blank');
      return;
    }
    var dataSource = images.map(function (image) {
      // The width/height attributes image_tag writes; the decoded size if a
      // slide was rendered without them, or PhotoSwipe gets NaN and opens blank.
      var w = parseInt(image.getAttribute('width'), 10) || image.naturalWidth || 1200;
      var h = parseInt(image.getAttribute('height'), 10) || image.naturalHeight || 1200;
      return {
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: w,
        height: h,
        alt: image.alt,
        thumbCropped: true
      };
    });
    lightbox.loadAndOpen(picked.index, dataSource);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-pdp-v2-gallery-zoom], [data-pdp-v2-gallery-track] .pdp-v2-gallery__slide > img');
    if (!trigger) return;
    var gallery = galleryOf(trigger);
    if (gallery) zoomOpen(gallery);
  });

  // Swiping the track moves the dots. Capture, because scroll does not bubble.
  document.addEventListener(
    'scroll',
    function (event) {
      var track = event.target;
      if (!track || !track.hasAttribute || !track.hasAttribute('data-pdp-v2-gallery-track')) return;
      var gallery = galleryOf(track);
      if (gallery) galleryMark(gallery, galleryIndex(gallery));
      clearTimeout(track.pdpV2Settle);
      track.pdpV2Settle = setTimeout(function () { galleryLoop(track); }, 150);
    },
    { capture: true, passive: true }
  );

  /* ---- pack selector --------------------------------------------------- */
  // 'sachets' and 'perday' only exist as data-* attributes on a pack option
  // when the product has a flavour axis and the merchant has set "Sachets per
  // month" -- see main-product-pdp-v2.liquid. Missing on every other product,
  // where this loop's `value === null` check below already skips them, same
  // as any other bind key a simpler product's markup does not carry.
  var PACK_BINDINGS = ['label', 'total', 'mrp', 'save', 'pct', 'sachets', 'perday', 'nudge'];

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-pdp-v2-pack-toggle]');
    if (toggle) {
      if (toggle.closest('[data-pdp-v2-pack-open="always"]')) return;
      toggle.setAttribute('aria-expanded', toggle.getAttribute('aria-expanded') === 'true' ? 'false' : 'true');
      return;
    }

    var option = event.target.closest('[data-pdp-v2-pack-option]');
    if (option) {
      selectPack(option);
      pack1Popup(option);
      return;
    }

    // Anywhere else closes an open panel ("Always open" ones excepted).
    document.querySelectorAll('[data-pdp-v2-pack]:not([data-pdp-v2-pack-open="always"]) [data-pdp-v2-pack-toggle][aria-expanded="true"]').forEach(function (open) {
      open.setAttribute('aria-expanded', 'false');
    });
  });

  /* Buy box v2 "Upgrade to N bottles" line: picks the pack after the selected
     one. On the last pack it is a statement, not a link. */
  document.addEventListener('click', function (event) {
    var nudge = event.target.closest('[data-pdp-v2-nudge]');
    if (!nudge || nudge.classList.contains('is-best')) return;
    var root = nudge.closest('[data-pdp-v2-hero]');
    var options = root ? Array.prototype.slice.call(root.querySelectorAll('[data-pdp-v2-pack-option]')) : [];
    var next = options[options.indexOf(root.querySelector('[data-pdp-v2-pack-option].is-active')) + 1];
    if (next && !next.disabled) next.click();
  });

  /* Verification certificate by batch: the API answers { url } with a
     presigned PDF. The tab is opened during the tap (a popup opened after the
     fetch is blocked on phones) and pointed at the PDF once it arrives. */
  document.addEventListener('click', function (event) {
    var coa = event.target.closest('[data-pdp-v2-coa]');
    if (!coa) return;
    event.preventDefault();
    var tab = window.open('', '_blank');
    fetch(coa.getAttribute('data-pdp-v2-coa'))
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data || !data.url) throw new Error('no certificate url');
        if (tab) tab.location.href = data.url;
        else window.location.href = data.url;
      })
      .catch(function () {
        if (tab) tab.close();
        alert('The certificate could not be loaded. Please try again.');
      });
  });

  /* Buy box v2 "Ask AI": the store's BotSpace chat (its public
     BotspaceChat.open(), which queues until the widget is ready). Without the
     widget on the page the chip stays a plain link to WhatsApp.
     BotspaceChat only appears ~1s after DOMContentLoaded (later on phones); a
     tap before that, while the app embed's loader (__rexWidgetLoader) is on
     the page, waits for it instead of falling through to WhatsApp. */
  function botspaceReady() {
    return window.BotspaceChat && typeof window.BotspaceChat.open === 'function';
  }

  document.addEventListener('click', function (event) {
    var ask = event.target.closest('[data-pdp-v2-askai]');
    if (!ask) return;
    if (botspaceReady()) {
      event.preventDefault();
      window.BotspaceChat.open({});
      return;
    }
    if (!window.__rexWidgetLoader) return;
    event.preventDefault();
    // ponytail: 8s cap, then WhatsApp in this tab (a popup after the wait would be blocked)
    var waited = 0;
    var timer = setInterval(function () {
      waited += 200;
      if (botspaceReady()) {
        clearInterval(timer);
        window.BotspaceChat.open({});
      } else if (waited >= 8000) {
        clearInterval(timer);
        if (ask.href && ask.getAttribute('href') !== '#') window.location.href = ask.href;
      }
    }, 200);
  });

  /* Bulk order form (Shopify Forms, in its own shadow root). Restyled to the
     prototype's form: static labels above 48px fields, the country picker
     joined to the phone field, a 50px button, the prototype's placeholders.
     The page stylesheet can't reach into the shadow root, so the rules go in
     it. The app's class names carry build hashes (_formFieldContainer_1mxsl_5),
     hence the [class*=] prefixes; the app also writes an inline `top: 23px` on
     every label on focus, which the !important top overrides.
     ponytail: rides on the app's markup; if an app update renames these
     classes the form falls back to its own (working) look. */
  var BULK_FORM_CSS =
    // Visible, so the quantity dropdown can open over what's below the form.
    'section[class*="_inline_"],section[class*="_formContainer_"]{overflow:visible!important}' +
    'section[class*="_formContainer_"]{max-width:none!important;width:100%!important;margin:0!important}' +
    // 2px inset: the field borders are outer box-shadows, and the panel clips.
    'form{gap:14px!important;padding:0 2px 2px!important;--form-field-border:0 0 0 1px #E1E6F0;--form-field-border-focused:0 0 0 1px #1F54C4;--form-field-border-radius:12px;--form-text-color:#0B1220;--form-placeholder-color:#8A909C}' +
    '[class*="_formFieldContainer_"],[class*="_formPhoneInputContainer_"]{position:relative;padding-top:18px}' +
    '[class*="_formPhoneInputContainer_"]{grid-template-columns:auto 1fr!important;gap:0!important}' +
    '[class*="_formPhoneInputContainer_"] [class*="_formFieldContainer_"]{position:static;padding-top:0}' +
    'label[class*="_formInputFieldLabel_"]{position:absolute;top:0!important;left:0!important;transform:none!important;font:12px/1 "SF-Pro-Display-Bold",sans-serif!important;color:#6E758A!important;max-width:none}' +
    'input[class*="_formInputField_"],input[class*="_formPhoneInputField_"]{box-sizing:border-box!important;width:100%!important;height:48px!important;padding:0 14px!important;font:15px/normal "SF-Pro-Display-Regular",sans-serif!important;color:#0B1220!important}' +
    'input::placeholder{color:#8A909C!important;opacity:1!important}' +
    'input[type=number]{-moz-appearance:textfield}' +
    'input[type=number]::-webkit-inner-spin-button,input[type=number]::-webkit-outer-spin-button{-webkit-appearance:none;margin:0}' +
    '[class*="_formPhoneInputContainer_"] input{border-radius:0 12px 12px 0!important}' +
    '[class*="_formPhoneInputContainer_"] [class*="_selectToggle_"]{box-sizing:border-box!important;height:48px!important;max-height:none!important;padding:0 12px!important;gap:6px;align-items:center;background:#F7F9FC!important;border-radius:12px 0 0 12px!important}' +
    // The flag's wrapper is narrower than the flag and clips it.
    '[class*="_formPhoneInputContainer_"] [class*="_selectToggleText_"]{overflow:visible!important;width:auto!important;flex:none!important}' +
    // Quantity: the app's custom dropdown (the direct-child select; the phone's
    // country picker is nested deeper). Always opens downward, like a native select.
    'form>[class*="_selectContainer_"] p[class*="_label_"]{margin:0 0 6px!important;font:12px/1 "SF-Pro-Display-Bold",sans-serif!important;color:#6E758A!important}' +
    'form>[class*="_selectContainer_"] [class*="_selectToggle_"]{box-sizing:border-box!important;height:48px!important;max-height:none!important;padding:0 14px!important;border-radius:12px!important;font:15px/normal "SF-Pro-Display-Regular",sans-serif!important;color:#0B1220!important;align-items:center}' +
    'form>[class*="_selectContainer_"] [class*="_selectToggleText_"]:empty::before{content:"Select quantity";color:#8A909C}' +
    'form>[class*="_selectContainer_"] [class*="_dropdownContainer_"]{border-radius:12px!important;box-shadow:0 0 0 1px #E1E6F0,0 10px 24px rgba(11,18,32,.12)!important;overflow:hidden!important;top:100%!important;bottom:auto!important;margin-top:6px!important;max-height:none!important}' +
    'form>[class*="_selectContainer_"] [role=listbox]{max-height:none!important}' +
    'form>[class*="_selectContainer_"] [role=option]{padding:12px 14px!important;font:15px/1.3 "SF-Pro-Display-Regular",sans-serif!important;color:#0B1220!important}' +
    'form>[class*="_selectContainer_"] [role=option]:hover,form>[class*="_selectContainer_"] [role=option]:focus{background:#F5F7FA!important}' +
    'form>[class*="_selectContainer_"] [role=option][aria-selected=true]{color:#1F54C4!important;font-family:"SF-Pro-Display-Bold",sans-serif!important}' +
    'button[type=submit]{box-sizing:border-box!important;height:50px!important;padding:0 16px!important;margin-top:4px;border-radius:12px!important;font:15px/1 "SF-Pro-Display-Bold",sans-serif!important;justify-content:center!important;align-items:center!important}' +
    // Success: the prototype's green "Request received" box with its check.
    '[data-testid="banner-success"]{position:relative;margin:14px 0 0!important;padding:12px 12px 12px 44px!important;background:#E9F7EF!important;border-radius:12px!important;text-align:left!important}' +
    '[data-testid="banner-success"]::before{content:"";position:absolute;left:12px;top:12px;width:22px;height:22px;background:url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Ccircle cx=\'12\' cy=\'12\' r=\'10\' fill=\'%230F7A3D\'/%3E%3Cpath d=\'M7.5 12.3l3 3 6-6.3\' stroke=\'%23fff\' stroke-width=\'2\' fill=\'none\' stroke-linecap=\'round\' stroke-linejoin=\'round\'/%3E%3C/svg%3E") no-repeat center/contain}' +
    '[data-testid="banner-success"] h2{margin:0!important;padding:0!important;font:13.5px/22px "SF-Pro-Display-Bold",sans-serif!important;color:#0B1220!important;text-align:left!important}' +
    '[data-testid="banner-success"] h2+div,[data-testid="banner-success"] h2+div *{margin:2px 0 0!important;font:12px/1.5 "SF-Pro-Display-Regular",sans-serif!important;color:#31384A!important;text-align:left!important}';

  var BULK_PLACEHOLDERS = {
    '[id="first_name"]': 'Enter your full name',
    'input[type="tel"]': '00000 00000',
    '[id="email"]': 'Enter your email',
    'input[type="number"]': 'Number of units'
  };

  function bulkPlaceholders(sr) {
    Object.keys(BULK_PLACEHOLDERS).forEach(function (sel) {
      var input = sr.querySelector(sel);
      if (input && !input.getAttribute('placeholder')) input.setAttribute('placeholder', BULK_PLACEHOLDERS[sel]);
    });
  }

  /* The prototype's success line names the person, number and quantity. The
     app's success text (set in Shopify Forms) is generic, so the values are
     noted on submit and written into its text once the banner appears. */
  function bulkNote(sr) {
    var val = function (sel) {
      var el = sr.querySelector(sel);
      return el ? (el.value !== undefined ? el.value : el.textContent).trim() : '';
    };
    return {
      first: val('[id="first_name"]').split(/\s+/)[0],
      phone: val('input[type="tel"]'),
      qty: val('form>[class*="_selectContainer_"] [class*="_selectToggleText_"]')
    };
  }

  function bulkSuccess(sr, note) {
    var text = sr.querySelector('[data-testid="banner-success"] h2+div');
    if (!text || !note || text.hasAttribute('data-pdp-v2')) return;
    text.setAttribute('data-pdp-v2', '');
    // A phone left at the prefilled "+91" counts as no number.
    var phone = note.phone.replace(/\D/g, '').length > 4 ? note.phone : '';
    text.textContent =
      'Thanks' + (note.first ? ', ' + note.first : '') + '. Our team will call you' +
      (phone ? ' on ' + phone : '') + ' with bulk pricing' + (note.qty ? ' for ' + note.qty : '') + '.';
  }

  /* Tells support which PDP the request came from: a single-line text question
     whose label starts with "Product" (added in Shopify Forms) is filled with
     this page's product name and hidden. The app is Preact, so the value goes
     through the native setter plus an input event, or its state never sees it.
     No such question on the form, nothing happens. */
  var setInputValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;

  function bulkProduct(sr, name) {
    if (!name) return;
    sr.querySelectorAll('input[type="text"]').forEach(function (input) {
      var ids = (input.getAttribute('aria-labelledby') || '').split(' ');
      var label = sr.getElementById(ids[0]) || (input.labels && input.labels[0]);
      if (!label || !/^\s*product\b/i.test(label.textContent) || /quantit/i.test(label.textContent)) return;
      var field = input.closest('[class*="_formFieldContainer_"]') || input.parentElement;
      field.style.display = 'none';
      if (input.value === name) return;
      setInputValue.call(input, name);
      input.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    });
  }

  function fixBulkForm() {
    document.querySelectorAll('.pdp-v2-bulk__form shopify-forms-embed').forEach(function (embed) {
      var sr = embed.shadowRoot;
      if (!sr || sr.querySelector('style[data-pdp-v2]')) return;
      var product = embed.closest('.pdp-v2-bulk__form').getAttribute('data-product');
      var style = document.createElement('style');
      style.setAttribute('data-pdp-v2', '');
      style.textContent = BULK_FORM_CSS;
      sr.appendChild(style);
      bulkPlaceholders(sr);
      bulkProduct(sr, product);
      var note = null;
      // Capture: the form is the app's, and it may stop the event.
      sr.addEventListener('submit', function () {
        note = bulkNote(sr);
      }, true);
      // The app renders its fields after the shadow root exists, again after a
      // submit error or a restart, and swaps in the success banner. childList
      // only, so the attribute and text writes here don't re-trigger it.
      new MutationObserver(function () {
        bulkPlaceholders(sr);
        bulkProduct(sr, product);
        bulkSuccess(sr, note);
      }).observe(sr, { childList: true, subtree: true });
    });
  }

  function initBulkForm() {
    document.querySelectorAll('.pdp-v2-bulk__form').forEach(function (box) {
      if (!claim(box)) return;
      fixBulkForm();
      // The Forms loader adds the embed after this runs.
      new MutationObserver(fixBulkForm).observe(box, { childList: true, subtree: true });
    });
  }

  /* The old PDP's pack-of-1 nudge, same rules as pdp-custom.js: the first
     click on the first pack, once per page load, only with more than one pack.
     The popup markup is the theme's (pack1-alert-placeholder), and so is its
     close handling -- pdp-custom.js binds the X and the overlay. */
  function pack1Popup(option) {
    var pack = option.closest('[data-pdp-v2-pack1-popup]');
    if (!pack || pack.__pdpV2Pack1Shown) return;
    var options = pack.querySelectorAll('[data-pdp-v2-pack-option]');
    if (options.length < 2 || options[0] !== option) return;

    var overlay = document.getElementById('packOverlay');
    var popup = document.getElementById('packPopup');
    var main = popup && popup.querySelector('.pack-popup-main-para');
    var sub = popup && popup.querySelector('.pack-popup-sub-para');
    if (!overlay || !main || !sub) return;

    pack.__pdpV2Pack1Shown = true;
    main.textContent = pack.getAttribute('data-content1');
    sub.textContent = pack.getAttribute('data-content2');
    overlay.style.display = 'flex';
    overlay.setAttribute('aria-hidden', 'false');
    popup.style.display = 'flex';
    document.body.classList.add('no-scroll-while-popup');
  }

  function selectPack(option) {
    var root = option.closest('[data-pdp-v2-hero]');
    var pack = option.closest('[data-pdp-v2-pack]');
    if (!root || !pack) return;

    // Every price string was rendered by Liquid and parked on the option, so
    // nothing here formats currency -- what is shown is what the cart charges.
    PACK_BINDINGS.forEach(function (key) {
      var value = option.getAttribute('data-' + key);
      if (value === null) return;
      root.querySelectorAll('[data-pdp-v2-bind="' + key + '"]').forEach(function (target) {
        target.textContent = value;
      });
    });

    // Packs are variants, so this is what actually goes in the cart. There is
    // no quantity input to keep in step -- a pack is one line, not N lines.
    var input = root.querySelector('[data-pdp-v2-variant-input]');
    if (input) input.value = option.getAttribute('data-variant-id');

    packAvailability(root, option);
    packMedia(root, option, 'smooth');

    pack.querySelectorAll('[data-pdp-v2-pack-option]').forEach(function (other) {
      var on = other === option;
      other.classList.toggle('is-active', on);
      other.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    // Buy box v2's line under the tiles: its text is bound above; an empty
    // one (next pack sold out) hides it, and the last pack turns it green.
    var nudge = root.querySelector('[data-pdp-v2-nudge]');
    if (nudge) {
      nudge.hidden = !option.getAttribute('data-nudge');
      nudge.classList.toggle('is-best', option.hasAttribute('data-nudge-best'));
    }

    var toggle = pack.querySelector('[data-pdp-v2-pack-toggle]');
    if (toggle && pack.getAttribute('data-pdp-v2-pack-open') !== 'always') toggle.setAttribute('aria-expanded', 'false');

    // On a flavoured product this pack row's baked-in id/media/availability
    // were rendered for whichever flavour was selected at page load, so the
    // combo map gets the final say for the flavour actually selected now. On
    // a product with no flavour axis comboOf() finds no map and this is a
    // no-op -- nothing above changes for it.
    resolveCombo(root);
  }

  /* ---- flavour selector -------------------------------------------------
     Flavour never moves the price -- only which exact variant (and picture)
     the *currently selected pack* resolves to. So picking a flavour never
     touches the price/label bindings a pack row already carries; it only
     asks the combo map for the id that (this pack, this flavour) points to. */
  document.addEventListener('click', function (event) {
    var option = event.target.closest('[data-pdp-v2-flavour-option]');
    if (!option) return;
    selectFlavour(option);
  });

  function selectFlavour(option) {
    var root = option.closest('[data-pdp-v2-hero]');
    var flavour = option.closest('[data-pdp-v2-flavour]');
    if (!root || !flavour) return;

    flavour.querySelectorAll('[data-pdp-v2-flavour-option]').forEach(function (other) {
      var on = other === option;
      other.classList.toggle('is-active', on);
      other.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    var value = option.getAttribute('data-flavour-value') || '';
    root.setAttribute('data-pdp-v2-current-flavour', value);

    // Before resolveCombo() below scrolls the gallery to this (pack, flavour)
    // pair's own picture -- that scroll has to land among the slides this
    // flavour actually shows, so the filter runs first.
    applyFlavourFilter(root);

    // The taxline under the price names the selected flavour alongside the
    // pack (see main-product-pdp-v2.liquid) -- pack and flavour are picked
    // independently, so this updates on its own rather than through
    // PACK_BINDINGS, which only fires from selectPack().
    root.querySelectorAll('[data-pdp-v2-bind="flavour"]').forEach(function (target) {
      target.textContent = value;
    });

    // The Subtitle setting can be written as one "Flavour · Tagline" line
    // per flavour (see main-product-pdp-v2.liquid); re-parsed here off the
    // span's own `data-default-subtitle` -- the RAW, un-split setting --
    // the same way that section does for the first paint, so only the line
    // matching the flavour just picked is shown. A Subtitle with no "·" at
    // all has nothing to match and comes back unchanged.
    root.querySelectorAll('[data-pdp-v2-bind="subtitle"]').forEach(function (target) {
      var raw = target.getAttribute('data-default-subtitle') || '';
      target.textContent = subtitleForFlavour(raw, value);
    });

    resolveCombo(root);
  }

  /* Mirrors the Liquid parse in main-product-pdp-v2.liquid exactly: split
     into lines, each optionally "Flavour · Tagline," (trailing comma
     optional), first line whose flavour matches wins. `raw` came out of an
     HTML attribute, so entities are already decoded -- no unescaping here. */
  function subtitleForFlavour(raw, value) {
    var lines = raw.split(/\r?\n/);
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].replace(/,\s*$/, '').trim();
      if (!line) continue;
      var sep = line.indexOf('·');
      if (sep === -1) continue;
      var lineFlavour = line.slice(0, sep).trim();
      if (lineFlavour === value) {
        return lineFlavour + ' · ' + line.slice(sep + 1).trim();
      }
    }
    return raw;
  }

  /* The combo map is a hidden JSON script per hero, keyed "pack||flavour" ->
     { id, available, media }. Parsed once and cached on the root, since it
     never changes after render -- only which entry is read does. */
  function comboOf(root) {
    if (root.__pdpV2Combo) return root.__pdpV2Combo;
    var script = root.querySelector('[data-pdp-v2-combo]');
    if (!script) return null;
    try {
      root.__pdpV2Combo = JSON.parse(script.textContent);
    } catch (e) {
      root.__pdpV2Combo = {};
    }
    return root.__pdpV2Combo;
  }

  function resolveCombo(root) {
    var combo = comboOf(root);
    if (!combo) return;

    var activePack = root.querySelector('[data-pdp-v2-pack-option].is-active');
    var packValue = activePack && activePack.getAttribute('data-pack-value');
    var flavourValue = root.getAttribute('data-pdp-v2-current-flavour');
    if (!packValue || !flavourValue) return;

    var entry = combo[packValue + '||' + flavourValue];
    if (!entry) return;

    var input = root.querySelector('[data-pdp-v2-variant-input]');
    if (input) input.value = entry.id;

    var button = root.querySelector('[data-pdp-v2-add]');
    if (button) {
      var sold = !entry.available;
      button.disabled = sold;
      var label = button.querySelector('[data-pdp-v2-add-label]');
      if (label) {
        var text = button.getAttribute(sold ? 'data-soldout-label' : 'data-instock-label');
        if (text) {
          label.textContent = text;
          label.setAttribute('data-original', text);
        }
      }
    }

    var gallery = root.querySelector('[data-pdp-v2-gallery]');
    if (gallery) galleryGo(gallery, entry.media, 'smooth');
  }

  /* First paint: the flavour marked is-active by Liquid has to be recorded as
     "current" before anything can be resolved against it. Runs on load and on
     a theme-editor re-render, same as initPack. */
  function initFlavour(root) {
    (root || document).querySelectorAll('[data-pdp-v2-flavour-option].is-active').forEach(function (option) {
      var hero = option.closest('[data-pdp-v2-hero]');
      if (!hero) return;
      hero.setAttribute('data-pdp-v2-current-flavour', option.getAttribute('data-flavour-value') || '');
      // Runs before initPack()'s own galleryGo, so the gallery is already
      // filtered to this flavour by the time that jump happens.
      applyFlavourFilter(hero);
    });
  }

  /* Stock is per pack now: one pack can be out while another is not, so the CTA
     follows the selection instead of being decided once at render. A sold-out
     pack can even be the one selected on arrival, via ?variant=. */
  function packAvailability(root, option) {
    var button = root.querySelector('[data-pdp-v2-add]');
    if (!button) return;

    var sold = option.getAttribute('data-available') === 'false';
    button.disabled = sold;

    var label = button.querySelector('[data-pdp-v2-add-label]');
    if (!label) return;
    var text = button.getAttribute(sold ? 'data-soldout-label' : 'data-instock-label');
    if (!text) return;
    label.textContent = text;
    // The add-confirmation restores whatever it captured the first time it ran.
    // Keeping it in step here stops a pack switch mid-confirmation putting the
    // previous pack's word back.
    label.setAttribute('data-original', text);
  }

  /* Each pack has its own picture. The gallery already holds every one of the
     product's media, so a pack is an index into the track that is there --
     nothing is filtered, re-rendered or fetched. */
  function packMedia(root, option, behavior) {
    var raw = option.getAttribute('data-media');
    if (raw === null) return;
    var index = Number(raw);
    if (isNaN(index)) return;
    var gallery = root.querySelector('[data-pdp-v2-gallery]');
    if (gallery) galleryGo(gallery, index, behavior);
  }

  /* First paint: the preselected pack is not necessarily the first slide, so
     the track has to be moved to meet the markup, which already marks the right
     dot and thumb. Runs on load and on a theme-editor re-render. */
  function initPack(root) {
    (root || document).querySelectorAll('[data-pdp-v2-pack-option].is-active').forEach(function (option) {
      var hero = option.closest('[data-pdp-v2-hero]');
      if (!hero) return;
      packAvailability(hero, option);
      packMedia(hero, option, 'auto');
    });

    // "Open once per browser session": open on the first visit to this
    // product, then never again until the session ends. Storage can throw
    // (private mode, blocked site data) -- then it just stays closed.
    (root || document).querySelectorAll('[data-pdp-v2-pack-open="session"]').forEach(function (pack) {
      var key = 'pdpV2PackOpened:' + pack.getAttribute('data-pdp-v2-pack-key');
      try {
        if (sessionStorage.getItem(key)) return;
        sessionStorage.setItem(key, '1');
      } catch (e) {
        return;
      }
      var toggle = pack.querySelector('[data-pdp-v2-pack-toggle]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
    });
  }

  /* ---- add to cart: confirmation, and no cart drawer (except collagen) -- */
  /* One handler for the hero CTA and the bought-with cards. ProductForm
     dispatches `variant:add` on the form once /cart/add.js comes back OK, so
     the confirmation is driven by the cart actually changing rather than by the
     click — a failed add leaves the button alone, which is the point. The
     pending state is CSS on `aria-busy` and needs nothing here.

     Capture phase on `document`, for two reasons: it runs before CartDrawer's
     and CartNotificationDrawer's own bubble-phase listeners on `document`, and
     both of them check `event.detail.blockCartDrawerOpening` before showing.
     Setting it here is the theme's own opt-out — quick-buy-drawer uses exactly
     the same flag. `cart:change` is dispatched separately on documentElement
     and is untouched, so the header cart count still updates. */
  function addButtonOf(form) {
    return form && form.querySelector ? form.querySelector('[data-pdp-v2-add]') : null;
  }

  function endAdding(button) {
    if (!button) return;
    clearTimeout(button._pdpV2Adding);
    button.classList.remove('is-adding');
  }

  /* `aria-busy` covers /cart/add.js only. ProductForm clears it there and then
     makes a second trip to /cart.js before dispatching `variant:add`, which
     left half a second where the button showed neither the loader nor the
     confirmation. `.is-adding` spans the whole operation instead. */
  document.addEventListener(
    'submit',
    function (event) {
      var button = addButtonOf(event.target);
      if (!button) return;
      button.classList.add('is-adding');
      // Failsafe: if neither variant:add nor cart:error ever arrives, the dots
      // must still stop. The attribute-driven half cannot get stuck; this half
      // can, so it gets its own deadline.
      clearTimeout(button._pdpV2Adding);
      button._pdpV2Adding = setTimeout(function () {
        button.classList.remove('is-adding');
      }, 15000);
    },
    true
  );

  document.addEventListener(
    'cart:error',
    function (event) {
      endAdding(addButtonOf(event.target));
    },
    true
  );

  document.addEventListener(
    'variant:add',
    function (event) {
      var form = event.target;
      if (!form || !form.querySelector) return;
      var button = form.querySelector('[data-pdp-v2-add]');
      if (!button) return;

      endAdding(button);
      // The hero's "Open the cart drawer after adding" setting opts back in
      // to the theme's cart drawer (`data-pdp-v2-open-cart`), for its own CTA
      // and the bought-with cards.
      if (event.detail && !document.querySelector('[data-pdp-v2-open-cart]')) {
        event.detail.blockCartDrawerOpening = true;
      }

      var label = button.querySelector('[data-pdp-v2-add-label]');
      if (!label) return;

      var original = label.getAttribute('data-original') || label.textContent.trim();
      label.setAttribute('data-original', original);
      label.textContent = button.getAttribute('data-added-label') || 'Added';
      button.classList.add('is-added');

      clearTimeout(button._pdpV2Timer);
      // 1500ms is the prototype's own timeout.
      button._pdpV2Timer = setTimeout(function () {
        label.textContent = original;
        button.classList.remove('is-added');
      }, 1500);
    },
    true
  );

  /* ---- copy to clipboard ----------------------------------------------- */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-pdp-v2-copy]');
    if (!button) return;

    var text = button.getAttribute('data-pdp-v2-copy');
    var label = button.querySelector('[data-pdp-v2-copy-label]');
    var done = function () {
      if (!label) return;
      var original = label.getAttribute('data-original') || label.textContent;
      label.setAttribute('data-original', original);
      label.textContent = button.getAttribute('data-copied-label') || 'Copied';
      button.classList.add('is-copied');
      // 1600ms is the prototype's own timeout.
      setTimeout(function () {
        label.textContent = original;
        button.classList.remove('is-copied');
      }, 1600);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, function () {});
      return;
    }
    // Older Safari on iOS: no async clipboard outside a secure context.
    var scratch = document.createElement('textarea');
    scratch.value = text;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.opacity = '0';
    document.body.appendChild(scratch);
    scratch.select();
    try {
      document.execCommand('copy');
      done();
    } catch (e) {
      /* clipboard unavailable; the code is still on screen to copy by hand */
    }
    document.body.removeChild(scratch);
  });

  /* ---- pincode ---------------------------------------------------------- */
  // Lives in assets/clickpost-edd.js now, shared with the old product page.

  /* ---- back to top ------------------------------------------------------- */

  // 400px is the design's threshold. Passive listener and a class toggle only,
  // so the scroll handler does no layout work of its own.
  function initToTop() {
    var btn = document.querySelector('[data-pdp-v2-totop]');
    if (!btn || !claim(btn)) return;

    var sync = function () {
      btn.classList.toggle('is-on', window.pageYOffset > 400);
    };
    window.addEventListener('scroll', sync, { passive: true });
    sync();

    btn.addEventListener('click', function () {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
    });
  }

  /* ---- the product title opens the formula ------------------------------ */
  //
  // Two things have to happen before the scroll lands, and both are already
  // built: `pdp-v2:reveal` opens the collapsible section (the same event the
  // section nav fires), and the formula's own "view all elements" toggle opens
  // the ingredient list. The scroll waits a frame so it measures the section at
  // its opened height rather than its closed one.
  //
  // CAPTURE phase, not bubbling. The Judge.me preview badge carries this
  // attribute, and the app binds its own click handler inside the badge; a
  // `stopPropagation()` in there means a bubbling listener on document never
  // sees the click at all, which is exactly how the badge came to do nothing.
  // Capture runs on the way DOWN, before any handler on a descendant, so the
  // app cannot swallow it. The other two openers — the pill and the title —
  // are unaffected: nothing else on the page listens for their clicks.
  document.addEventListener('click', function (event) {
    // The badge is matched by class as well as by the attribute. Judge.me
    // renders it after this script runs, and if the app re-renders the wrapper
    // rather than just filling it, our attribute goes with the old node — the
    // class is the app's own, so it is there either way. Delegation already
    // covers the badge simply arriving late.
    var opener = event.target.closest('[data-pdp-v2-openforms], .jdgm-widget.jdgm-preview-badge');
    if (!opener) return;

    // The selector has to be one that exists. It used to be '#pdp-v2-formula',
    // which is nobody's id: Shopify names the wrapper
    // 'shopify-section-template--<theme id>__pdp_v2_formula' and the section's
    // own root 'pdp-v2-formula-<section id>'. querySelector returned null and
    // the handler bailed, so the title and the pill had never worked. The
    // formula section is addressed by an attribute now — both variants carry
    // it, and only one of them may be on a product.
    // A badge matched by class alone has no attribute to read, and the reviews
    // are the only place it can lead.
    var selector = opener.getAttribute('data-pdp-v2-openforms') || '[data-pdp-v2-reviews]';
    var target = document.querySelector(selector);
    if (!target) return;

    // The desktop eyebrow pill is an <a href="#..."> so it still works with JS
    // off. With JS on the native hash jump would land before the section has
    // opened, and then fight the smooth scroll below.
    //
    // `closest` rather than a tagName test on the opener alone: the Judge.me
    // preview badge carries the attribute on OUR wrapper while the app renders
    // whatever it likes inside it, so the anchor that was clicked can sit below
    // the opener rather than being it.
    var link = event.target.closest('a');
    if (link && opener.contains(link)) event.preventDefault();

    target.dispatchEvent(new CustomEvent('pdp-v2:reveal', { bubbles: true }));

    // Whether the 7-elements list opens too is the merchant's call, per width,
    // because the two prototypes disagree: desktop's gotoFormula sets
    // showIngs:true, mobile's sets only secFormula:true. The formula section
    // carries the answer as `data-pdp-v2-openlist` — none / mobile / desktop /
    // both — so the choice lives with the thing being opened.
    var holder = target.matches('[data-pdp-v2-openlist]')
      ? target
      : target.querySelector('[data-pdp-v2-openlist]');
    var want = holder ? holder.getAttribute('data-pdp-v2-openlist') : 'none';

    if (want === 'both' || want === (mobile.matches ? 'mobile' : 'desktop')) {
      var list = target.querySelector('.pdp-v2-formula__list');
      var toggle = list && target.querySelector('[data-pdp-v2-accordion][aria-controls="' + list.id + '"]');
      if (toggle && toggle.getAttribute('aria-expanded') !== 'true') toggle.click();
    }

    // Land it under the header and the section nav rather than behind them —
    // the same measured offset the nav's own items use, so both ways of
    // reaching a section stop in the same place. A frame's wait lets the
    // section finish opening, so this measures the opened layout.
    setTimeout(function () {
      var scope = target.closest('.pdp-v2-shopify-section, .shopify-section') || target;
      var nav = document.querySelector('.pdp-secnav');
      var offset;
      if (nav && nav.parentElement) {
        // Templates carrying the section nav: read its wrapper's resolved
        // sticky top, which already sums the announcement bar and header, so
        // this matches what the nav's own chips do.
        offset = (parseFloat(getComputedStyle(nav.parentElement).top) || 0) + nav.offsetHeight;
      } else {
        // No section nav on this template — unchanged behaviour.
        var header = document.querySelector('store-header');
        offset = header ? header.offsetHeight : 0;
      }
      var top = scope.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }, 60);
  }, true);

  /* ---- usage tabs ------------------------------------------------------ */
  document.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-pdp-v2-tab]');
    if (!tab) return;

    var box = tab.closest('[data-pdp-v2-tabs]');
    if (!box) return;
    var index = tab.getAttribute('data-pdp-v2-tab');

    box.querySelectorAll('[data-pdp-v2-tab]').forEach(function (other) {
      var on = other === tab;
      other.classList.toggle('is-active', on);
      other.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    box.querySelectorAll('[data-pdp-v2-tab-panel]').forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-pdp-v2-tab-panel') !== index;
    });
  });

  /* ---- why-choose banner ----------------------------------------------- */
  document.addEventListener('click', function (event) {
    var cert = event.target.closest('[data-pdp-v2-why-cert]');
    if (!cert) return;

    // One click, no selection. Both `MOST final` prototypes wire every
    // certification straight to viewCert(i): a link opens in a new tab, an
    // image opens in the lightbox, and the banner copy never changes — whySel
    // stays -1 for the life of the page. An earlier round kept a select-then-
    // view step; the user has since confirmed the prototype is the intent, so
    // selectWhy(), Reset and the selected-circle state are gone with it.
    // Only the image needs JS. A certification that points at a page is a real
    // <a target="_blank"> now, so the browser opens it — which is also the only
    // way ⌘/Ctrl-click and middle-click can put it in a background tab.
    var image = cert.getAttribute('data-cert-image');
    if (!image) return;

    event.preventDefault();
    openCertbox(cert.closest('[data-pdp-v2-why]'), image, cert.getAttribute('data-cert-title'));
  });

  /* ---- the certificate lightbox ------------------------------------------ */

  function openCertbox(banner, src, title) {
    // From the section root, not banner.parentElement: the banner sits inside
    // .pdp-v2-why-wrap and the lightbox is that wrap's SIBLING, so a lookup
    // one level up searched the wrong subtree and found nothing. It looked
    // right only because the harness that checked it had no wrap.
    var box = (banner.closest('.pdp-v2') || document).querySelector('[data-pdp-v2-certbox]');
    if (!box) return;

    var img = box.querySelector('[data-pdp-v2-certbox-img]');
    var heading = box.querySelector('[data-pdp-v2-certbox-title]');
    if (img) {
      img.src = src;
      img.alt = title || '';
    }
    if (heading) heading.textContent = title || '';

    box.hidden = false;
    document.body.classList.add('pdp-v2-certbox-open');
    var close = box.querySelector('[data-pdp-v2-certbox-close]');
    if (close) close.focus();
  }

  function closeCertbox(box) {
    box.hidden = true;
    document.body.classList.remove('pdp-v2-certbox-open');
  }

  // The backdrop closes it, the sheet does not — so the click has to be ON the
  // backdrop itself, not merely inside it.
  document.addEventListener('click', function (event) {
    var close = event.target.closest('[data-pdp-v2-certbox-close]');
    if (close) return closeCertbox(close.closest('[data-pdp-v2-certbox]'));
    var box = event.target.closest('[data-pdp-v2-certbox]');
    if (box && event.target === box) closeCertbox(box);
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('[data-pdp-v2-certbox]:not([hidden])').forEach(closeCertbox);
  });

  /* ---- Judge.me review avatars ------------------------------------------
     Judge.me leaves .jdgm-rev__icon empty; the design shows the reviewer's
     initials there. Their widget renders after this script and re-renders on
     paging and sorting, so this observes the widget rather than running once.
     ---------------------------------------------------------------------- */
  function trimReviewAverage(root) {
    // Judge.me writes "4.95 out of 5"; the design shows the number alone. The
    // parent carries aria-label="Average rating is 4.95 stars", so trimming
    // the visible text costs a screen reader nothing.
    (root || document).querySelectorAll('.jdgm-rev-widg__summary-average').forEach(function (el) {
      var match = el.textContent.match(/[\d.]+/);
      if (match && el.textContent.trim() !== match[0]) el.textContent = match[0];
    });
  }

  function fillReviewInitials(root) {
    (root || document).querySelectorAll('.jdgm-rev__icon:empty').forEach(function (icon) {
      var card = icon.closest('.jdgm-rev');
      var author = card && card.querySelector('.jdgm-rev__author');
      var name = author ? author.textContent.trim() : '';
      if (!name) return;
      var parts = name.split(/\s+/).filter(Boolean);
      var initials = parts[0].charAt(0);
      if (parts.length > 1) initials += parts[parts.length - 1].charAt(0);
      icon.textContent = initials;
    });
  }

  function trimReviewCount(root) {
    // "Based on 19 reviews" -> "19 reviews", the design's caption.
    (root || document).querySelectorAll('.jdgm-rev-widg__summary-text').forEach(function (el) {
      var text = el.textContent.trim();
      if (/^Based on /i.test(text)) el.textContent = text.replace(/^Based on /i, '');
    });
  }

  function clampReviewBodies(root) {
    // The CSS clamps every review; the button is only worth adding where the
    // text is actually taller than the clamp. Measured on each pass rather than
    // once, because a late webfont can change where the text lands.
    (root || document).querySelectorAll('.jdgm-rev__body').forEach(function (body) {
      body.classList.add('pdp-v2-jdgm-clamp');
      if (body.classList.contains('is-open')) return;

      // Look for the button across the whole card, not just at
      // body.nextElementSibling: Judge.me re-renders the body and slips its own
      // .jdgm-rev__custom-form in between, which made the next pass think there
      // was no button and add a second one.
      var card = body.parentElement;
      var existing = card.querySelectorAll('.pdp-v2-jdgm-more');
      for (var i = 1; i < existing.length; i++) existing[i].remove();
      var button = existing[0];

      if (body.scrollHeight <= body.clientHeight + 1) {
        if (button) button.remove();
        return;
      }

      if (!button) {
        button = document.createElement('button');
        button.type = 'button';
        button.className = 'pdp-v2-jdgm-more';
        button.textContent = 'Read more';
      }
      // Only when it has drifted, or the move itself wakes the observer forever.
      if (body.nextElementSibling !== button) body.insertAdjacentElement('afterend', button);
    });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.pdp-v2-jdgm-more');
    if (!button) return;
    var body = button.previousElementSibling;
    if (!body) return;
    var open = body.classList.toggle('is-open');
    button.textContent = open ? 'Read less' : 'Read more';
  });

  function noPagingScroll() {
    return false;
  }

  // ...and then focuses the first card, which scrolls it into view. That focus
  // is NOT covered by `scrollTo` — jdgm._focusFirstPaginatedItem calls a bare
  // .focus() — so the page-size click below threw the visitor down to the
  // reviews about two seconds after load, on every page load. Same focus, with
  // preventScroll: the card still takes focus for a screen reader and a
  // keyboard, the viewport just stays where the visitor left it.
  function noPagingFocus($items) {
    var el = $items.find('.jdgm-rev, .jdgm-quest')[0];
    if (!el) return;
    el.setAttribute('tabindex', '-1');
    el.focus({ preventScroll: true });
  }

  function suppressPagingScroll() {
    // Judge.me smooth-scrolls the widget to the top of the viewport on every
    // page change, and then focuses the first card, which scrolls again. The
    // design swaps the three cards in place and never moves the page.
    // `jdgmSettings.scrollTo` is Judge.me's own opt-out hook: their paginate
    // code skips both jumps when it returns false. Set here rather than at
    // load because jdgmSettings may not exist yet when this file runs.
    if (window.jdgmSettings && window.jdgmSettings.scrollTo !== noPagingScroll) {
      window.jdgmSettings.scrollTo = noPagingScroll;
    }
    // An accessor rather than an assignment, because Judge.me's bundle assigns
    // this property again after `jdgm` first exists (measured on the preview:
    // the object appears at 0.6s and is re-populated at 1.2s, half a second
    // before the paginate response lands). A plain assignment made here in
    // between is overwritten and the jump comes back; a setter that ignores
    // theirs cannot lose the race, and ignoring rather than freezing keeps
    // their own assignment from throwing.
    if (window.jdgm && window.jdgm._focusFirstPaginatedItem !== noPagingFocus) {
      Object.defineProperty(window.jdgm, '_focusFirstPaginatedItem', {
        get: function () {
          return noPagingFocus;
        },
        set: function () {},
        configurable: true
      });
    }
  }

  // The design shows three review cards a page. The shop's Judge.me setting is
  // 10 and is shared with other pages, so it is overridden here rather than in
  // their dashboard.
  var REVIEWS_PER_PAGE = 3;

  // Judge.me binds its delegated pager handler a beat AFTER it renders the
  // pager — measured on the preview, the pager is in the DOM at 1.8s and the
  // click handler appears at 1.9s. A click in that window is swallowed with no
  // error, so latching on the *attempt* wins the race or loses it forever. It
  // started losing, and the widget stayed on the shop's 10 cards.
  var perPageTries = 0;

  function setReviewsPerPage(widget) {
    // Judge.me reads the page size off .jdgm-paginate with jQuery's .data() at
    // click time, and the response to that click rebuilds the pager for the new
    // size (19 reviews become 7 pages). So this is one value plus one click,
    // and their own machinery does everything else.
    var $ = window.jdgm && window.jdgm.$;
    var pag = widget.querySelector('.jdgm-paginate');
    if (!$ || !pag || widget.hasAttribute('data-pdp-v2-per-page')) return;

    // Their handler is bound to `.jdgm-paginate__page:not(.jdgm-curt)`, so page
    // one has to stop being the current page for exactly one click. The
    // response puts jdgm-curt back.
    var current = pag.querySelector('.jdgm-paginate__page.jdgm-curt');
    if (!current || perPageTries++ > 40) return;

    // `jdgm.beforeFetchingReviews` is fired on document from inside their click
    // handler, synchronously, before the request goes out — so by the time
    // .click() returns we know whether the click was taken. Only then is the
    // latch set; otherwise page one goes back exactly as it was and the next
    // pass tries again. Without this the widget is left with no current page at
    // all, which is how it was found: latched, pager stripped, still ten cards.
    var taken = false;
    var mark = function () {
      taken = true;
    };

    $(document).on('jdgm.beforeFetchingReviews', mark);
    $(pag).data('per-page', REVIEWS_PER_PAGE);
    current.classList.remove('jdgm-curt');
    current.click();
    $(document).off('jdgm.beforeFetchingReviews', mark);

    if (taken) {
      // Latch: the response is a second away and the observer fires many times
      // in between. The widget's attributes are not observed, so setting this
      // costs no extra pass.
      widget.setAttribute('data-pdp-v2-per-page', REVIEWS_PER_PAGE);
      return;
    }

    current.classList.add('jdgm-curt');
    // The observer only fires on DOM changes and the handler binding is not
    // one, so the retry needs its own tick. 40 tries at 200ms gives up after 8s.
    setTimeout(function () {
      setReviewsPerPage(widget);
    }, 200);
  }

  function revealNewReviews(widget) {
    // A page change replaces the cards. New ones come in on the same reveal the
    // rest of the page uses, staggered by column the way the prototype does it
    // (`transitionDelay = (i % 3) * 70ms`). Cards already carrying the class
    // are left alone, so the MutationObserver this runs under settles.
    var cards = widget.querySelectorAll('.jdgm-rev-widg__reviews > .jdgm-rev:not(.pdp-v2-reveal)');
    if (!cards.length) return;
    cards.forEach(function (card, i) {
      card.classList.add('pdp-v2-reveal');
      card.style.transitionDelay = (i % 3) * 70 + 'ms';
    });
    observeReveals(widget);
  }

  function watchReviews() {
    var widget = document.getElementById('judgeme_product_reviews');
    if (!widget) return;
    var apply = function () {
      // Scroll suppression first: the resize below pages the widget itself, and
      // that click would otherwise jump the viewport on first load.
      suppressPagingScroll();
      setReviewsPerPage(widget);
      fillReviewInitials(widget);
      trimReviewAverage(widget);
      trimReviewCount(widget);
      clampReviewBodies(widget);
      revealNewReviews(widget);
    };
    apply();
    new MutationObserver(apply).observe(widget, { childList: true, subtree: true });
    // The clamp measurement depends on the webfont, which lands after this.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(apply);
  }

  // Buy box v2 points pill. The amount is Nector's own, read off its widget's
  // data attribute so it follows every pack change; the rupee worth is that
  // times the section's point value. Until Nector has rendered, the pill stays
  // hidden and Nector's own line shows instead.
  function initPoints(root) {
    (root || document).querySelectorAll('[data-pdp-v2-points]').forEach(function (pill) {
      if (pill.dataset.pdpV2Ready) return;
      pill.dataset.pdpV2Ready = '1';
      var box = pill.parentElement;
      var rate = parseFloat(pill.dataset.value) || 0;
      var num = pill.querySelector('[data-pdp-v2-points-n]');
      var worth = pill.querySelector('[data-pdp-v2-points-worth]');
      var sync = function () {
        var src = box.querySelector('[data-coin-amount-customer-earns]');
        var n = src ? parseInt(src.getAttribute('data-coin-amount-customer-earns'), 10) : 0;
        if (!n) return;
        // Equality checks keep the observer below from re-triggering itself.
        if (num.textContent !== String(n)) num.textContent = n;
        var w = Math.round(n * rate).toLocaleString('en-IN');
        if (worth && worth.textContent !== w) worth.textContent = w;
        if (pill.hidden) pill.hidden = false;
      };
      sync();
      new MutationObserver(sync).observe(box, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['data-coin-amount-customer-earns']
      });
    });
  }

  /* ==========================================================================
     SECTION BEHAVIOUR  (both final prototypes, built 2026-08-27)

     The mobile page collapses five sections, rotates three others and swipes
     between the two facts cards. All of that is mobile-only except who it's
     for, which the desktop design builds the same way and which therefore
     ticks at every width — see autoRotate's third argument.

     Everything here writes CLASSES, never inline styles. That is the whole
     reason crossing 1150px costs nothing: a class the rules on the other side
     do not mention is inert, whereas an inline transform would have to be
     unwound on every resize and would fight the other layout until it was.
     ====================================================================== */

  function mark(list, index) {
    for (var i = 0; i < list.length; i++) list[i].classList.toggle('is-on', i === index);
  }

  // One-shot guard, so a scan can run on load and again on shopify:section:load
  // without binding a second set of listeners to what is already wired.
  function claim(el) {
    if (el.hasAttribute('data-pdp-v2-ready')) return false;
    el.setAttribute('data-pdp-v2-ready', '');
    return true;
  }

  // 30px and horizontal-dominant, the prototype's own threshold: a vertical
  // scroll that drifts sideways under the thumb must not page the carousel.
  function onSwipe(el, handler) {
    var x0 = null;
    var y0 = 0;
    el.addEventListener(
      'touchstart',
      function (event) {
        var touch = event.touches[0];
        if (!touch) return;
        x0 = touch.clientX;
        y0 = touch.clientY;
      },
      { passive: true }
    );
    el.addEventListener(
      'touchend',
      function (event) {
        var touch = event.changedTouches[0];
        if (!touch || x0 === null) return;
        var dx = touch.clientX - x0;
        var dy = touch.clientY - y0;
        x0 = null;
        if (Math.abs(dx) <= 30 || Math.abs(dx) <= Math.abs(dy)) return;
        // A swipe is followed by a click on whatever was under the thumb.
        // Stamp it so the tap handlers can tell the two apart.
        el.__pdpV2Swiped = Date.now();
        handler(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  }

  function swipedJustNow(el) {
    return Date.now() - (el.__pdpV2Swiped || 0) < 300;
  }

  // Gated on the media query rather than torn down and rebuilt on resize:
  // above 1150px the classes these write mean nothing, so "stop on desktop" is
  // exactly "do not tick", and there is no teardown to get wrong.
  //
  // It does NOT stop for prefers-reduced-motion. It used to, and that was too
  // blunt: iOS "Reduce Motion" is switched on by a lot of people, and stopping
  // the tick left four of the five who-panels unreachable unless you knew to
  // tap. The panels still change; pdp-v2.css drops the transitions under that
  // query instead, so the content cycles with no motion at all — which is what
  // the setting is actually asking for, and every rotator here pauses on the
  // first tap regardless.
  //
  // `everywhere` is for the one rotator both designs share: who it's for
  // cycles at 1440 as well as at 360, so it must not be gated on the width.
  function autoRotate(every, step, everywhere) {
    return setInterval(function () {
      if (everywhere || mobile.matches) step();
    }, every);
  }

  /* ---- collapsible sections -------------------------------------------- */

  function setSection(toggle, open) {
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

    // A header can drive a second accordion as well as its own panel — the
    // formula heading opens the ingredient list. Clicking that accordion's own
    // button rather than reimplementing it keeps one height animation and one
    // set of aria attributes for every accordion on the page.
    var also = toggle.getAttribute('data-pdp-v2-sec-also');
    if (also) {
      var inner = document.querySelector('[data-pdp-v2-accordion][aria-controls="' + also + '"]');
      if (inner && (inner.getAttribute('aria-expanded') === 'true') !== open) inner.click();
    }

    var head = toggle.closest('.pdp-v2-sechead');
    if (head && head.parentElement) head.parentElement.classList.toggle('is-sec-open', open);
    // The reviews band is THREE sibling sections — our heading, the Judge.me
    // app block and the quote — so its panel cannot be a sibling div of the
    // toggle the way the other four are. A body class is the only hook that
    // reaches all three at once.
    var key = toggle.getAttribute('data-pdp-v2-sec');
    if (key) document.body.classList.toggle('pdp-v2-sec-' + key + '-open', open);
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-pdp-v2-sec]');
    if (!toggle) return;
    setSection(toggle, toggle.getAttribute('aria-expanded') !== 'true');
  });

  // The section nav scrolls to a section that is very likely closed. The
  // prototype opens it first; without this the tap lands on an 86px header
  // with nothing under it, which reads as a broken link.
  document.addEventListener('pdp-v2:reveal', function (event) {
    var target = event.target;
    var toggles = document.querySelectorAll('[data-pdp-v2-sec]');
    for (var i = 0; i < toggles.length; i++) {
      // Shopify's section wrapper when there is one, our own section root when
      // there is not — the theme editor and a bare render both have to work,
      // and the nav can resolve to any node inside either.
      var head = toggles[i].closest('.pdp-v2-sechead');
      var scope = toggles[i].closest('.pdp-v2-shopify-section') || (head && head.parentElement);
      if (scope && scope.contains(target)) {
        setSection(toggles[i], true);
        return;
      }
    }
    // Reviews again: the nav resolves to the widget or the quote, and neither
    // sits inside the section that owns the toggle.
    if (!target.closest('.shopify-section--apps, .pdp-v2-testimonial-section')) return;
    var reviews = document.querySelector('[data-pdp-v2-sec="reviews"]');
    if (reviews) setSection(reviews, true);
  });

  /* ---- who it's for: the image accordion --------------------------------- */

  // The one section both final designs build the same way — five panels, one
  // open, `flex: 6` against `flex: 1`. Mobile stacks them and opens on tap;
  // desktop lays them across and opens on hover as well, resuming the cycle
  // when the pointer leaves. Same nodes, same classes, same JS.
  function initWho(root) {
    var panels = root.querySelectorAll('.pdp-v2-who__panel');
    if (panels.length < 2) return;

    var i = 0;
    var paused = false;
    var show = function (n) {
      i = (n + panels.length) % panels.length;
      mark(panels, i);
    };

    show(0);
    // 7s — the slowest of the rotators, because each panel carries a paragraph
    // rather than a label. `MOST final` stopped the mobile cycle (`_whoTick`
    // is null there) and kept the desktop one, so this ticks only above 1150px:
    // on a phone the panels change when you tap them and not before.
    autoRotate(
      7000,
      function () {
        if (!paused && !mobile.matches) show(i + 1);
      },
      true
    );
    onSwipe(root, function (dir) {
      paused = true;
      show(i + dir);
    });

    // Desktop resumes the cycle when the pointer leaves the strip; a tap on
    // mobile stops it for good, which is what the mobile design does — there
    // is no "leave" on a touchscreen to resume on.
    root.addEventListener('mouseleave', function () {
      if (!mobile.matches) paused = false;
    });

    for (var n = 0; n < panels.length; n++) {
      (function (index) {
        panels[index].addEventListener('click', function () {
          if (swipedJustNow(root)) return;
          paused = true;
          show(index);
        });
        panels[index].addEventListener('mouseenter', function () {
          if (mobile.matches) return;
          paused = true;
          show(index);
        });
      })(n);
    }
  }

  /* ---- shop with confidence: coverflow ----------------------------------- */

  function initConfidence(root) {
    // The "Grid" mobile layout (data-pdp-v2-conf-layout="grid") has no
    // coverflow: every card sits in a plain 2-column grid and flips in place
    // via the shared handler above, so none of the centring/rotation
    // machinery below applies to it -- it only needs its own height sync.
    if (root.getAttribute('data-pdp-v2-conf-layout') === 'grid') {
      initConfidenceGrid(root);
      return;
    }

    var cards = root.querySelectorAll('.pdp-v2-conf__card');
    var dots = root.querySelectorAll('.pdp-v2-conf__dot');
    if (cards.length < 2) return;

    var grid = root.querySelector('.pdp-v2-conf__grid');
    var count = cards.length;
    var i = 0;
    var paused = false;

    // The coverflow container has to size itself in JS: its cards are
    // absolutely positioned (for the slide/scale transform) and so don't
    // contribute to its height, and only the centre card's active face
    // (front or back) should decide how tall it is.
    var syncHeight = function () {
      if (!grid || !mobile.matches) return;
      var center = root.querySelector('.pdp-v2-conf__card.is-center');
      if (!center) return;
      var expanded = center.getAttribute('aria-expanded') === 'true';
      var face = center.querySelector(expanded ? '.pdp-v2-conf__face--back' : '.pdp-v2-conf__face--front');
      if (face) grid.style.height = face.scrollHeight + 'px';
    };

    var show = function (n) {
      i = (n + count) % count;
      for (var j = 0; j < count; j++) {
        var offset = (j - i + count) % count;
        var card = cards[j];
        card.classList.toggle('is-center', offset === 0);
        card.classList.toggle('is-right', offset === 1);
        card.classList.toggle('is-left', offset === count - 1 && count > 2);
        // A card carried off the centre stops being flipped, or it would slide
        // away showing its back and come round again still showing it.
        if (offset !== 0) card.setAttribute('aria-expanded', 'false');
      }
      mark(dots, i);
      syncHeight();
    };

    show(0);
    autoRotate(4500, function () {
      if (!paused) show(i + 1);
    });

    // A tap that flips the centre card (rather than re-centring a side one)
    // changes its aria-expanded without going through show(), so the height
    // needs its own listener too.
    var flipObserver = new MutationObserver(syncHeight);
    for (var m = 0; m < cards.length; m++) {
      flipObserver.observe(cards[m], { attributes: true, attributeFilter: ['aria-expanded'] });
    }

    var onBreakpointChange = function () {
      if (mobile.matches) {
        syncHeight();
      } else if (grid) {
        grid.style.height = '';
      }
    };
    if (mobile.addEventListener) mobile.addEventListener('change', onBreakpointChange);
    else mobile.addListener(onBreakpointChange);

    for (var k = 0; k < count; k++) {
      (function (index) {
        cards[index].addEventListener('click', function () {
          paused = true;
          if (!mobile.matches) return;
          if (!cards[index].classList.contains('is-center')) show(index);
        });
      })(k);
    }

    for (var d = 0; d < dots.length; d++) {
      (function (index) {
        dots[index].addEventListener('click', function () {
          paused = true;
          show(index);
        });
      })(d);
    }

    // Swipe by hand on mobile: a horizontal drag moves one card left/right.
    // The click that follows a swipe is swallowed (capture phase) so the
    // drag doesn't also flip or re-centre the card it ended on.
    if (grid) {
      var startX = 0;
      var startY = 0;
      var tracking = false;
      var swiped = false;

      grid.addEventListener('touchstart', function (e) {
        if (!mobile.matches || e.touches.length !== 1) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
        swiped = false;
      }, { passive: true });

      grid.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        var t = e.changedTouches[0];
        var dx = t.clientX - startX;
        var dy = t.clientY - startY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        swiped = true;
        paused = true;
        show(dx < 0 ? i + 1 : i - 1);
      }, { passive: true });

      grid.addEventListener('click', function (e) {
        if (!swiped) return;
        swiped = false;
        e.preventDefault();
        e.stopPropagation();
      }, true);
    }
  }

  /* ---- shop with confidence: static grid --------------------------------- */

  // The "Grid" mobile layout sizes every card to whichever one needs the most
  // room, rather than a guessed fixed number: a merchant's longer title or
  // caption on one card would otherwise either leave the others with dead
  // space (guessed too tall) or get clipped (guessed too short). A plain CSS
  // grid can't do this on its own -- it only equalizes height WITHIN a row,
  // not across the two rows a 2x2 layout has -- so this measures every card
  // at its own natural height and sets them all to the tallest one found.
  function initConfidenceGrid(root) {
    var grid = root.querySelector('.pdp-v2-conf__grid--static');
    if (!grid) return;

    var sync = function () {
      var cards = grid.querySelectorAll('.pdp-v2-conf__card');
      if (!cards.length) return;

      // Desktop never renders this as a grid at all -- .pdp-v2-conf__grid--
      // static has no rules above 1150px, so the section falls back to the
      // ordinary 4-across row, which already sizes itself in CSS.
      if (!mobile.matches) {
        cards.forEach(function (card) {
          card.style.height = '';
        });
        return;
      }

      // Release any height a previous pass set before measuring, or every
      // card would just report that old shared height back.
      cards.forEach(function (card) {
        card.style.height = '';
      });
      var max = 0;
      cards.forEach(function (card) {
        max = Math.max(max, card.getBoundingClientRect().height);
      });
      cards.forEach(function (card) {
        card.style.height = max + 'px';
      });
    };

    sync();

    // Rotating the phone, or a webfont swapping in after the first measure,
    // can both change how a card's text wraps and so how tall it needs to be.
    if (mobile.addEventListener) mobile.addEventListener('change', sync);
    else mobile.addListener(sync);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(sync);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(sync, 150);
    });
  }

  /* ---- supplement facts: two-slide swiper -------------------------------- */

  function initFacts(root) {
    var track = root.querySelector('.pdp-v2-facts');
    var dots = root.querySelectorAll('.pdp-v2-facts__dot');
    if (!track || dots.length < 2) return;

    // CSS scroll-snap does the swiping, the momentum and the rubber-banding.
    // The prototype hand-rolls a transform track with its own touch deltas;
    // this is the same gesture with none of that code, and it keeps working
    // for a trackpad, a scrollbar drag and a keyboard.
    var sync = function () {
      mark(dots, Math.round(track.scrollLeft / Math.max(1, track.clientWidth)));
    };

    track.addEventListener('scroll', sync, { passive: true });
    for (var i = 0; i < dots.length; i++) {
      (function (index) {
        dots[index].addEventListener('click', function () {
          track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
        });
      })(i);
    }
    sync();
  }

  function initMobileUi() {
    document.querySelectorAll('[data-pdp-v2-who]').forEach(function (el) {
      if (claim(el)) initWho(el);
    });
    document.querySelectorAll('[data-pdp-v2-conf]').forEach(function (el) {
      if (claim(el)) initConfidence(el);
    });
    document.querySelectorAll('[data-pdp-v2-facts]').forEach(function (el) {
      if (claim(el)) initFacts(el);
    });
    initToTop();
  }

  /* ---- scroll reveal --------------------------------------------------- */
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function observeReveals(root) {
    var targets = (root || document).querySelectorAll('.pdp-v2-reveal:not(.is-in)');
    if (!targets.length) return;

    if (reduce.matches || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  // A header rendered `aria-expanded="true"` has to have its classes applied
  // once at load — the reviews band is three sibling sections driven by a body
  // class, so nothing is open until setSection has run for it.
  function syncOpenSections(root) {
    (root || document)
      .querySelectorAll('[data-pdp-v2-sec][aria-expanded="true"]')
      .forEach(function (toggle) {
        setSection(toggle, true);
      });
  }

  function start() {
    observeReveals(document);
    syncOpenSections(document);
    watchReviews();
    initMobileUi();
    initFlavour(document);
    initPack(document);
    initPoints(document);
    initBulkForm();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Theme editor: a re-rendered section arrives with fresh, unrevealed nodes.
  document.addEventListener('shopify:section:load', function (event) {
    observeReveals(event.target);
    syncOpenSections(event.target);
    initMobileUi();
    initFlavour(event.target);
    initPack(event.target);
    initPoints(event.target);
    initBulkForm();
  });
})();